import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { devWarn } from "@/lib/logger";

/**
 * PortfolioGallery — up to 5 before/after / gig images on a freelancer profile.
 *
 * Backend contract:
 *   GET    /api/uploads/portfolio          — owner's items
 *   GET    /api/uploads/portfolio/of/:uid  — public list (marketplace cards)
 *   POST   /api/uploads/portfolio          — multipart upload, 200 or 409-at-cap
 *   DELETE /api/uploads/portfolio/:id      — soft-delete one
 *
 * Modes:
 *   `readOnly=true` — public marketplace view: no upload/delete buttons, fetches from
 *   the `/of/:uid` endpoint. Pass `userId` in this mode.
 *
 *   `readOnly=false` (default) — owner view: full CRUD, fetches from `/portfolio`.
 */
export const PortfolioGallery = ({ readOnly = false, userId = null }) => {
  const [items, setItems] = useState([]);
  const [max, setMax] = useState(5);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const resolveUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = readOnly ? `/uploads/portfolio/of/${userId}` : "/uploads/portfolio";
      const r = await api.get(endpoint);
      setItems(r?.data?.items || []);
      if (r?.data?.max) setMax(r.data.max);
    } catch (err) {
      devWarn("[Portfolio] load failed:", err?.message || err);
    } finally {
      setLoading(false);
    }
  }, [readOnly, userId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, PNG, WEBP or GIF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/uploads/portfolio", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Portfolio image added");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed. Please retry.";
      devWarn("[Portfolio] upload failed:", detail);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (assetId) => {
    if (!window.confirm("Remove this portfolio image?")) return;
    setBusy(true);
    try {
      await api.delete(`/uploads/portfolio/${assetId}`);
      toast.success("Removed");
      setItems((prev) => prev.filter((it) => it.id !== assetId));
    } catch (err) {
      devWarn("[Portfolio] delete failed:", err?.message || err);
      toast.error("Remove failed. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  const remaining = max - items.length;
  const canUpload = !readOnly && remaining > 0 && !busy;

  return (
    <div data-testid="portfolio-gallery">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono">
          // portfolio {readOnly ? "" : `· ${items.length}/${max}`}
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={!canUpload}
            className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#60a5fa] hover:bg-[#93c5fd] disabled:bg-zinc-700 disabled:text-zinc-400 px-3 py-1.5 font-black transition-colors"
            data-testid="portfolio-upload-btn"
          >
            {busy ? "…" : remaining > 0 ? "+ Add image" : "Slots full"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-xs font-mono text-zinc-500 py-8 text-center border border-dashed border-zinc-800">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs font-mono text-zinc-500 py-8 text-center border border-dashed border-zinc-800" data-testid="portfolio-empty">
          {readOnly ? "No portfolio images yet." : "Add before/after shots, mock-ups or gig samples so clients trust your work."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {items.map((it) => (
            <div key={it.id} className="relative group aspect-square overflow-hidden border border-zinc-800" data-testid={`portfolio-item-${it.id}`}>
              <img
                src={resolveUrl(it.url)}
                alt={it.caption || "portfolio"}
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
              {!readOnly && (
                <button
                  onClick={() => remove(it.id)}
                  disabled={busy}
                  className="absolute top-1 right-1 bg-black/80 hover:bg-red-500 text-white text-[9px] tracking-[0.2em] uppercase font-black px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-30"
                  data-testid={`portfolio-remove-${it.id}`}
                  title="Remove"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          data-testid="portfolio-file-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload(f);
            e.target.value = "";  // reset so re-uploading the same file fires onChange
          }}
        />
      )}

      {!readOnly && items.length > 0 && (
        <div className="mt-3 text-[10px] font-mono text-zinc-500">
          JPG, PNG, WEBP, or GIF · max 5 MB each · up to {max} images
        </div>
      )}
    </div>
  );
};

export default PortfolioGallery;
